'use client'

import {
    getUnassignedClients,
    getWallets,
    SalesWallet,
    transferClients,
    UnassignedClient,
} from '@/lib/api/sales'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import { MdArrowBack, MdGroups, MdMoveDown } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { formatPhoneNumber } from '@/lib/form-state/util'

const PAGE_SIZE = 50

/**
 * Pool "Sem carteira": clientes que ainda não pertencem a nenhuma carteira —
 * porque chegaram por um caminho sem carteira elegível, ou porque a carteira
 * deles foi excluída. Daqui eles são atribuídos em massa.
 */
export default function UnassignedClientsPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canUpdate } = useResourcePermissions('sales', 'wallets')

    const [loading, setLoading] = useState(true)
    const [clients, setClients] = useState<UnassignedClient[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)

    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [targetWalletId, setTargetWalletId] = useState<string>('')
    const [transferring, setTransferring] = useState(false)

    useEffect(() => {
        if (user?.organizationId) {
            loadClients()
            loadWallets()
        }
    }, [user?.organizationId, page])

    const loadClients = async () => {
        setLoading(true)
        try {
            const response = await getUnassignedClients(page, PAGE_SIZE)
            if (response.success && response.result) {
                setClients(response.result.items || [])
                setTotal(response.result.total || 0)
                setSelectedIds([])
            } else {
                toast.error(response.error || 'Erro ao carregar clientes')
            }
        } catch {
            toast.error('Erro ao carregar clientes')
        } finally {
            setLoading(false)
        }
    }

    const loadWallets = async () => {
        try {
            const response = await getWallets()
            if (response.success) {
                setWallets((response.result || []).filter(w => w.active))
            }
        } catch {
            // lista de destino é opcional no carregamento inicial
        }
    }

    const toggleSelect = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    const toggleSelectAll = () => {
        setSelectedIds(prev =>
            prev.length === clients.length ? [] : clients.map(c => c.id)
        )
    }

    const handleTransfer = async () => {
        if (selectedIds.length === 0 || !targetWalletId) return

        setTransferring(true)
        try {
            const response = await transferClients(
                selectedIds,
                parseInt(targetWalletId, 10)
            )
            if (response.success) {
                toast.success(
                    `${response.result?.moved ?? selectedIds.length} cliente(s) movidos para a carteira`
                )
                setTargetWalletId('')
                loadClients()
            } else {
                toast.error(response.error || 'Erro ao mover clientes')
            }
        } catch {
            toast.error('Erro ao mover clientes')
        } finally {
            setTransferring(false)
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={() => navigate('/sales/wallets')}
                        title="Voltar para carteiras"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-warning/70 flex items-center justify-center shadow-lg">
                        <MdGroups className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                Clientes sem carteira
                            </h1>
                            <span className="badge badge-sm badge-warning">{total}</span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Clientes que ainda não pertencem a nenhuma carteira
                        </p>
                    </div>
                </div>
            </div>

            {/* Barra de atribuição em massa */}
            {canUpdate && clients.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 bg-base-100 rounded-xl border border-base-300/50 p-4">
                    <div className="form-control w-full sm:max-w-xs">
                        <label className="label pb-1">
                            <span className="label-text text-sm font-medium">
                                Carteira de destino
                            </span>
                        </label>
                        <select
                            className="select select-bordered select-sm w-full"
                            value={targetWalletId}
                            onChange={e => setTargetWalletId(e.target.value)}
                        >
                            <option value="">Selecione...</option>
                            {wallets.map(w => (
                                <option key={w.id} value={w.id}>
                                    {w.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        className="btn btn-secondary btn-sm gap-2"
                        onClick={handleTransfer}
                        disabled={transferring || selectedIds.length === 0 || !targetWalletId}
                    >
                        {transferring ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdMoveDown className="text-base" />
                        )}
                        Mover {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}cliente(s)
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-md text-secondary" />
                </div>
            ) : clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdGroups className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum cliente no pool
                        </h3>
                        <p className="text-sm text-base-content/60">
                            Todos os clientes da organização estão em uma carteira
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                {canUpdate && (
                                    <th className="w-10">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-sm checkbox-secondary"
                                            checked={
                                                clients.length > 0 &&
                                                selectedIds.length === clients.length
                                            }
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                )}
                                <th>Cliente</th>
                                <th>E-mail</th>
                                <th>WhatsApp</th>
                                <th>Cadastrado em</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.map(client => (
                                <tr key={client.id}>
                                    {canUpdate && (
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-sm checkbox-secondary"
                                                checked={selectedIds.includes(client.id)}
                                                onChange={() => toggleSelect(client.id)}
                                            />
                                        </td>
                                    )}
                                    <td className="font-medium">{client.name}</td>
                                    <td className="text-base-content/60">
                                        {client.email || '-'}
                                    </td>
                                    <td className="text-base-content/60">
                                        {client.whatsAppNumber ? formatPhoneNumber(client.whatsAppNumber) : '-'}
                                    </td>
                                    <td className="text-base-content/60">
                                        {format(new Date(client.createdAt), 'dd/MM/yyyy', {
                                            locale: ptBR,
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                    >
                        Anterior
                    </button>
                    <span className="text-sm text-base-content/60">
                        Página {page} de {totalPages}
                    </span>
                    <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    )
}
